import React, { useEffect, useRef, useState } from 'react';
import ProjectPicker from './ProjectPicker';
import {
  endOf, floorToMinute, moveToDate, resolveWindow, toDateInputValue, toTimeInputValue, MAX_DURATION_MINUTES,
} from './entryTime';

/** The text for a window that could not be read, shared with the row's inline fields. */
export const NONEXISTENT_TIME_MESSAGE = "That time doesn't exist on that day";

/**
 * The move dialog: project, date, start and end in one place, for the widths where the
 * row hides those controls (below `lg`). It makes the same single `fn_effort_correct` the
 * inline fields make, through the same `onSubmit`, so the same overlap, future and range
 * rules apply.
 *
 * Unlike an inline start edit (which holds the end fixed), the fields here are an absolute
 * window: the start is the date at the start time, and the end is the next time the clock
 * reads the end time after it. Only what differs from the entry is sent.
 *
 * Full-screen on a phone, a centred card from `md`. Esc closes.
 *
 * @param {object} props
 * @param {{at: string, minutes: number, what: string, client?: string, client_id?: string, task?: string, task_id?: string}} props.entry
 *   The entry as the row currently holds it.
 * @param {'project'|'date'|'time'} props.focus Which field to focus first.
 * @param {{minDate: string, maxDate: string}} props.bounds The first loaded day and today.
 * @param {() => Promise<{id: string, name: string}[]>} props.loadClients
 * @param {(clientId: string) => Promise<{id: string, title: string}[]>} props.loadTasks
 * @param {(change: object) => Promise<{status: 'saved'|'failed', message?: string}>} props.onSubmit
 * @param {() => void} props.onClose
 * @param {string} props.idPrefix
 */
export default function EntryMoveDialog({ entry, focus, bounds, loadClients, loadTasks, onSubmit, onClose, idPrefix }) {
  const initial = {
    day: toDateInputValue(entry.at),
    start: toTimeInputValue(entry.at),
    end: toTimeInputValue(endOf(entry)),
  };
  const [fields, setFields] = useState(initial);
  const [project, setProject] = useState(null);
  // A free-text label chosen in the picker; independent of the client and task.
  const [label, setLabel] = useState(null);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const dateRef = useRef(null);
  const startRef = useRef(null);
  const projectRef = useRef(null);

  useEffect(() => {
    const target = { date: dateRef, time: startRef, project: projectRef }[focus] || dateRef;
    target.current?.focus();
  }, [focus]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const set = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));
  const dayChanged = fields.day !== initial.day;
  const clockChanged = fields.start !== initial.start || fields.end !== initial.end;
  const timeDirty = dayChanged || clockChanged;
  const dirty = timeDirty || project !== null || label !== null;
  // A start and end cannot express a day or more (the window would be read as under 24
  // hours), so such an entry can move to another day but its time is not edited here.
  const longEntry = (Number(entry.minutes) || 0) >= MAX_DURATION_MINUTES;

  const submit = async () => {
    if (saving || !dirty) return;
    if (!entry.what?.trim()) {
      setError('Enter a description');
      return;
    }

    const change = { minutes: Number(entry.minutes) || 0, description: entry.what };
    if (timeDirty) {
      if (!fields.day) {
        setError('Pick a date');
        return;
      }
      if (dayChanged && (fields.day < bounds.minDate || fields.day > bounds.maxDate)) {
        setError('Pick a date within the days shown on this timesheet');
        return;
      }

      // Only a time field that was edited re-reads the clock times: the untouched start and
      // end are lossy displays (no seconds, never a day or more), so a date-only move keeps
      // the entry's own start and minutes and shifts the day.
      let start;
      let minutes = Number(entry.minutes) || 0;
      if (clockChanged) {
        const resolved = resolveWindow(fields);
        if (resolved.error) {
          setError(resolved.error === 'nonexistent' ? NONEXISTENT_TIME_MESSAGE : 'Enter a valid start and end time');
          return;
        }
        start = resolved.start;
        minutes = resolved.minutes;
      }
      // A start the user did not touch keeps its seconds, as the inline date control does.
      if (!clockChanged || fields.start === initial.start) {
        const moved = moveToDate(entry.at, fields.day);
        if (moved.error) {
          setError(moved.error === 'nonexistent' ? NONEXISTENT_TIME_MESSAGE : 'Pick a valid date');
          return;
        }
        start = moved.start;
      }
      const startMoved = floorToMinute(start.getTime()) !== floorToMinute(new Date(entry.at).getTime());
      if (startMoved || minutes !== (Number(entry.minutes) || 0)) {
        change.minutes = minutes;
        if (startMoved) change.when = start.toISOString();
      }
    }
    if (project) {
      if (project.clientId !== entry.client_id) change.clientId = project.clientId;
      if (project.taskId !== undefined && project.taskId !== entry.task_id) change.taskId = project.taskId;
      change.names = {
        clientId: project.clientId,
        client: project.clientName,
        taskId: project.taskId ?? null,
        task: project.taskName ?? null,
        service: label ?? entry.service,
      };
    }
    if (label !== null && label !== (entry.service ?? '').trim()) {
      change.service = label;
      change.names = change.names ?? {
        clientId: entry.client_id,
        client: entry.client,
        taskId: entry.task_id,
        task: entry.task,
        service: label,
      };
    }
    if (change.when === undefined && change.minutes === (Number(entry.minutes) || 0)
      && change.clientId === undefined && change.taskId === undefined && change.service === undefined) {
      onClose();
      return;
    }

    setError(null);
    setSaving(true);
    let result;
    try {
      result = await onSubmit(change);
    } catch (e) {
      result = { status: 'failed', message: e?.message };
    }
    setSaving(false);
    if (result?.status === 'saved') onClose();
    else setError(result?.message || 'Could not save this change.');
  };

  const projectLabel = project
    ? [project.clientName, project.taskName].filter(Boolean).join(' · ')
    : [entry.client, entry.task].filter(Boolean).join(' · ') || 'No project';
  // The free-text label shown now: the one just chosen, else the entry's.
  const shownLabel = label ?? (entry.service ?? '').trim();

  const FIELD = 'w-full rounded-md border border-gray-200 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm focus:border-[#32B1FF] focus:outline-none focus:ring-1 focus:ring-[#32B1FF] disabled:opacity-60';
  const LABEL = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 md:flex md:items-center md:justify-center md:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Change entry"
      data-testid="move-dialog"
    >
      <div className="h-full w-full overflow-auto bg-white dark:bg-gray-900 p-5 md:h-auto md:max-w-lg md:rounded-2xl md:border md:dark:border-gray-800 md:shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Change entry</h2>
        <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">{entry.what}</p>

        <div className="mt-4 space-y-4">
          <div>
            <span className={LABEL}>Project</span>
            {picking ? (
              <ProjectPicker
                entry={entry}
                loadClients={loadClients}
                loadTasks={loadTasks}
                idPrefix={idPrefix}
                onClose={() => setPicking(false)}
                onChoose={(choice) => {
                  if (choice.label !== undefined) setLabel(choice.label);
                  else setProject(choice);
                  setPicking(false);
                }}
              />
            ) : (
              <div className="flex items-center justify-between gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-2 py-1.5">
                <span className="min-w-0 truncate text-sm text-gray-700 dark:text-gray-200" data-testid="move-project-label">{projectLabel}</span>
                {shownLabel && (
                  <span
                    data-testid="move-service"
                    className="max-w-[9rem] shrink-0 truncate rounded bg-sky-100 dark:bg-sky-900/30 px-1.5 py-0.5 text-[11px] font-medium text-sky-800 dark:text-sky-300"
                  >
                    <span className="sr-only">Label: </span>
                    {shownLabel}
                  </span>
                )}
                <button
                  ref={projectRef}
                  type="button"
                  onClick={() => setPicking(true)}
                  disabled={saving}
                  data-testid="move-project"
                  className="shrink-0 rounded-md px-2 py-1 text-sm font-medium text-[#32B1FF] hover:bg-[#32B1FF]/10 disabled:opacity-60"
                >
                  Change
                </button>
              </div>
            )}
          </div>

          <div>
            <label className={LABEL} htmlFor={`${idPrefix}-day`}>Date</label>
            <input
              ref={dateRef}
              id={`${idPrefix}-day`}
              type="date"
              value={fields.day}
              min={bounds.minDate}
              max={bounds.maxDate}
              onChange={set('day')}
              disabled={saving}
              data-testid="move-date"
              className={FIELD}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL} htmlFor={`${idPrefix}-start`}>Start</label>
              <input
                ref={startRef}
                id={`${idPrefix}-start`}
                type="time"
                value={fields.start}
                onChange={set('start')}
                disabled={saving || longEntry}
                data-testid="move-start"
                className={FIELD}
              />
            </div>
            <div>
              <label className={LABEL} htmlFor={`${idPrefix}-end`}>End</label>
              <input
                id={`${idPrefix}-end`}
                type="time"
                value={fields.end}
                onChange={set('end')}
                disabled={saving || longEntry}
                data-testid="move-end"
                className={FIELD}
              />
            </div>
          </div>
        </div>

        {longEntry && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-testid="move-long-note">
            This entry is a day or more long, so its times can't be edited here. You can still move it to another day.
          </p>
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400" data-testid="move-error">{error}</p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="move-cancel"
            className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || !dirty}
            data-testid="move-save"
            className="rounded-md bg-[#32B1FF] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#2ba2ea] disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
